/**
 * Simple blog post parser for markdown files with YAML front matter.
 * No external dependencies — handles the subset of YAML and Markdown we need.
 */

import { resolveImagePath } from '@/utils/imageMap.ts';

export interface BlogPost {
  title: string;
  slug: string;
  date: string;
  labels: string[];
  image: string;
  thumbnail: string;
  imageAlt: string;
  snippet: string;
  contentHtml: string;
}

/**
 * Parse YAML front matter from a markdown string.
 * Returns the front matter as a record and the remaining body.
 */
function parseFrontMatter(raw: string): { meta: Record<string, unknown>; body: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) {
    return { meta: {}, body: raw };
  }

  const yamlBlock = match[1];
  const body = match[2];
  const meta: Record<string, unknown> = {};
  let currentKey = '';
  let listItems: string[] | null = null;

  for (const line of yamlBlock.split('\n')) {
    const listMatch = line.match(/^\s+-\s+(.+)$/);
    if (listMatch && currentKey) {
      listItems = listItems || [];
      listItems.push(listMatch[1].trim());
      continue;
    }

    // Flush previous list
    if (listItems && currentKey) {
      meta[currentKey] = listItems;
      listItems = null;
    }

    const kvMatch = line.match(/^(\w[\w-]*):\s*(.*)$/);
    if (kvMatch) {
      currentKey = kvMatch[1];
      const value = kvMatch[2].trim();
      if (value) {
        // Strip surrounding quotes
        meta[currentKey] = value.replace(/^["']|["']$/g, '');
      }
    }
  }

  // Flush trailing list
  if (listItems && currentKey) {
    meta[currentKey] = listItems;
  }

  return { meta, body };
}

/**
 * Very lightweight Markdown → HTML for blog body text.
 * Handles paragraphs, bold, italic, inline code, and em-dashes.
 */
function markdownToHtml(md: string): string {
  const paragraphs = md.trim().split(/\n{2,}/);

  return paragraphs
    .map((p) => {
      const trimmed = p.trim();

      // Standalone image: ![alt](src)
      const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) {
        const alt = imgMatch[1];
        const src = resolveImagePath(imgMatch[2]);
        return `<figure><img src="${src}" alt="${alt}" class="w-full rounded-lg shadow-md" />${alt ? `<figcaption class="text-sm text-gray-500 text-center mt-2">${alt}</figcaption>` : ''}</figure>`;
      }

      let html = trimmed.replace(/\n/g, ' ');
      // Inline images within text
      html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt: string, src: string) => {
        const resolved = resolveImagePath(src);
        return `<img src="${resolved}" alt="${alt}" class="w-full rounded-lg shadow-md my-4" />`;
      });
      // Links [text](url)
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>');
      // Bold
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Italic
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
      // Inline code
      html = html.replace(/`(.+?)`/g, '<code>$1</code>');
      // Em-dash
      html = html.replace(/ --- /g, ' &mdash; ');
      html = html.replace(/ -- /g, ' &mdash; ');
      return `<p>${html}</p>`;
    })
    .join('\n');
}

/**
 * Parse a single raw markdown string into a BlogPost.
 */
export function parseBlogPost(raw: string): BlogPost {
  const { meta, body } = parseFrontMatter(raw);

  return {
    title: (meta.title as string) || 'Untitled',
    slug: (meta.slug as string) || '',
    date: (meta.date as string) || '',
    labels: Array.isArray(meta.labels) ? (meta.labels as string[]) : [],
    image: (meta.image as string) || '',
    thumbnail: (meta.thumbnail as string) || '',
    imageAlt: (meta.imageAlt as string) || '',
    snippet: (meta.snippet as string) || '',
    contentHtml: markdownToHtml(body),
  };
}

/**
 * Load all blog posts from the data/blog directory using Vite's import.meta.glob.
 * Returns posts sorted by date descending (newest first).
 */
export function loadBlogPosts(): BlogPost[] {
  const modules = import.meta.glob('/src/data/blog/*.md', { eager: true, query: '?raw', import: 'default' });

  const posts: BlogPost[] = [];
  for (const raw of Object.values(modules)) {
    posts.push(parseBlogPost(raw as string));
  }

  posts.sort((a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : 0));
  return posts;
}

/**
 * Collect all unique labels across all posts.
 */
export function getAllLabels(posts: BlogPost[]): string[] {
  const labelSet = new Set<string>();
  for (const post of posts) {
    for (const label of post.labels) {
      labelSet.add(label);
    }
  }
  return Array.from(labelSet).sort();
}
